import { Graphics, Rectangle, Sprite, type Texture } from "pixi.js";
import { ActiveRuleset } from "@common/definitions/activeRuleset";
import { GameConstants, ObjectCategory, ZIndexes } from "@common/constants";
import { ArmorType } from "@common/definitions/items/armors";
import { PerkCategories, PerkIds } from "@common/definitions/items/perks";
import { type LootDefinition } from "@common/definitions/loots";
import { CircleHitbox } from "@common/utils/hitbox";
import { EaseFunctions } from "@common/utils/math";
import { DefinitionType } from "@common/utils/objectDefinitions";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import { isCollectibleSkin } from "@common/utils/skinCollection";
import { bagRoomFor } from "@common/utils/bagFill";
import { gunSwapSlot } from "@common/utils/gunPickup";
import { gearPickup, maxScopeCopies } from "@common/utils/spareGear";
import { type Vector } from "@common/utils/vector";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { PerkManager } from "../managers/perkManager";
import { UIManager } from "../managers/uiManager";
import { DIFF_LAYER_HITBOX_OPACITY, HITBOX_COLORS } from "../utils/constants";
import { DebugRenderer } from "../utils/debugRenderer";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { type Tween } from "../utils/tween";
import { GameObject } from "./gameObject";
import { LOOT_CENTRES } from "./lootCentres";
import { type Player } from "./player";

/**
 * The tier-3 halo, drawn once per size into a texture that every such loot shares: as a shape it would be some 700
 * vertices per loot, every frame.
 */
const haloTextures = new Map<number, Texture>();
const haloTexture = (radius: number): Texture => {
    const r = Math.round(radius);
    let texture = haloTextures.get(r);
    if (texture) return texture;
    const glow = new Graphics();
    for (const [grow, alpha] of [[18, 0.07], [13, 0.11], [9, 0.16], [5, 0.24]] as const) {
        glow.circle(0, 0, r + grow).fill({ color: 0xf0c05a, alpha });
    }
    glow.circle(0, 0, r + 1.5).stroke({ width: 3, color: 0xffd36a, alpha: 0.95 });
    glow.circle(0, 0, r - 3).stroke({ width: 1.5, color: 0xfff1c2, alpha: 0.45 });
    const extent = r + 20;
    texture = Game.pixi.renderer.generateTexture({ target: glow, frame: new Rectangle(-extent, -extent, extent * 2, extent * 2), resolution: 2, antialias: true });
    glow.destroy();
    haloTextures.set(r, texture);
    return texture;
};

export class Loot extends GameObject.derive(ObjectCategory.Loot) {
    private _glow?: Sprite;
    definition!: LootDefinition;

    readonly images: {
        readonly background: TrenchioSprite
        readonly item: TrenchioSprite
        readonly skinFistLeft: TrenchioSprite
        readonly skinFistRight: TrenchioSprite
    };

    private _count = 0;
    get count(): number { return this._count; }

    hitbox!: CircleHitbox;

    animation?: Tween<Vector>;

    constructor(id: number, data: ObjectsNetData[ObjectCategory.Loot]) {
        super(id);

        this.container.zIndex = ZIndexes.Loot;

        this.images = {
            background: new TrenchioSprite(),
            item: new TrenchioSprite(),
            skinFistLeft: new TrenchioSprite(),
            skinFistRight: new TrenchioSprite()
        };

        this.updateFromData(data, true);
    }

    override updateFromData(data: ObjectsNetData[ObjectCategory.Loot], isNew = false): void {
        if (data.full) {
            const definition = this.definition = data.full.definition;
            const idString = definition.idString;
            const defType = definition.defType;

            this.container.addChild(this.images.background, this.images.item);

            if (defType === DefinitionType.Skin) {
                this.images.item
                    .setFrame(definition.baseImage ?? `${idString}_base`)
                    .setPos(0, -3)
                    .setScale(0.65)
                    .setAngle(90)
                    .setTint(definition.baseTint ?? 0xffffff);

                const skinFist = definition.fistImage ?? `${idString}_fist`;
                this.images.skinFistLeft
                    .setFrame(skinFist)
                    .setPos(22, 20)
                    .setScale(0.65)
                    .setAngle(90)
                    .setTint(definition.fistTint ?? 0xffffff);
                this.images.skinFistRight
                    .setFrame(skinFist)
                    .setPos(-22, 20)
                    .setScale(0.65)
                    .setAngle(90)
                    .setTint(definition.fistTint ?? 0xffffff);

                if (definition.grassTint) {
                    const ghillieTint = Game.colors.ghillie;
                    this.images.item.setTint(ghillieTint);
                    this.images.skinFistLeft.setTint(ghillieTint);
                    this.images.skinFistRight.setTint(ghillieTint);
                }

                this.container.addChild(this.images.skinFistLeft, this.images.skinFistRight);
            } else {
                this.images.item.setFrame(idString);
                if (defType === DefinitionType.Gun) {
                    // guns lie on their bubble turned 30 degrees, muzzle up (their pictures are drawn level); the
                    // firework launcher keeps its original picture, which is drawn already turned, so it keeps 15
                    this.images.item.setAngle(idString === "firework_launcher" ? -15 : -30);
                }
            }

            const { backgroundTexture, scale } = Loot.getBackgroundAndScale(definition);
            if (backgroundTexture) {
                this.images.background.setFrame(backgroundTexture);
            } else {
                this.images.background.setVisible(false);
            }
            if (scale) this.images.item.setScale(scale);
            if (defType === DefinitionType.Gun) {
                // centred on its bubble by the middle of its smallest enclosing circle, turned and scaled like the
                // picture (see LOOT_CENTRES): a long magazine no longer pushes the gun itself off to one side
                const [dx, dy] = LOOT_CENTRES[idString] ?? [0, 0];
                const turn = this.images.item.rotation;
                const k = this.images.item.scale.x;
                this.images.item.setPos(
                    -(dx * Math.cos(turn) - dy * Math.sin(turn)) * k,
                    -(dx * Math.sin(turn) + dy * Math.cos(turn)) * k
                );
            }

            // tier-3 guns get a soft gold halo so the rare end of the loot pool reads at a glance
            this._glow?.destroy();
            this._glow = undefined;
            if (defType === DefinitionType.Gun && (ActiveRuleset.gunTiers.tier3 as readonly string[]).includes(idString)) {
                const r = Math.max(this.images.background.width, this.images.background.height) / 2 || 40;
                const glow = new Sprite(haloTexture(r));
                glow.anchor.set(0.5);
                this.container.addChildAt(glow, 0);
                this._glow = glow;
            }

            this.hitbox = new CircleHitbox(GameConstants.lootRadius[defType]);

            /*
                Infinity is serialized as 0 in the bit stream
                0 isn't a valid count value on the server
                thus
                If we receive 0 here, it must mean the count on
                the server is Infinity (or NaN lol) (or a decimal number, lmao)
            */
            this._count = data.full.count || Infinity;

            // Play an animation if this is new loot
            if (data.full.isNew && isNew) {
                this.container.scale.set(0);
                this.animation = Game.addTween({
                    target: this.container.scale,
                    to: { x: 1, y: 1 },
                    duration: 1000,
                    ease: EaseFunctions.elasticOut2,
                    onComplete: () => {
                        this.animation = undefined;
                    }
                });
            }
        }

        this.position = data.position;
        if (this.layer !== data.layer) {
            this.layer = data.layer;
            this.updateLayer();
        }
        this.hitbox.position = this.position;

        if (!GameConsole.getBuiltInCVar("cv_movement_smoothing") || isNew) {
            this.container.position = toPixiCoords(this.position);
        }
    }

    static getBackgroundAndScale(definition?: LootDefinition): { backgroundTexture?: string, scale?: number } {
        switch (definition?.defType) {
            case DefinitionType.Gun:
                return {
                    backgroundTexture: `loot_background_gun_${definition.ammoType}`,
                    scale: 0.85
                };

            case DefinitionType.Melee:
                return {
                    backgroundTexture: "loot_background_melee",
                    scale: definition.image?.lootScale
                };

            case DefinitionType.HealingItem:
                return { backgroundTexture: "loot_background_healing" };

            case DefinitionType.Armor:
            case DefinitionType.Backpack:
            case DefinitionType.Scope:
            case DefinitionType.Skin:
                return { backgroundTexture: "loot_background_equipment" };

            case DefinitionType.Throwable:
                return { backgroundTexture: "loot_background_throwable" };

            case DefinitionType.Perk:
                return {
                    backgroundTexture: definition.mechanical
                        ? "loot_background_mechanical_perk"
                        : "loot_background_perk"
                };

            case DefinitionType.Ammo:
                // wood gets a ring like other loot, so it stands out from the mud
                return definition.idString === "wood" ? { backgroundTexture: "loot_background_wood", scale: 0.95 } : {};

            default:
                return {};
        }
    }

    override updateDebugGraphics(): void {
        if (!DEBUG_CLIENT) return;
        if (!GameConsole.getBuiltInCVar("db_show_hitboxes_loot")) return;

        DebugRenderer.addHitbox(
            this.hitbox,
            HITBOX_COLORS.loot,
            this.layer === Game.layer ? 1 : DIFF_LAYER_HITBOX_OPACITY
        );
    }

    override update(): void { /* bleh */ }
    override updateInterpolation(): void {
        this.updateContainerPosition();
    }

    destroy(): void {
        super.destroy();
        this.images.background.destroy();
        this.images.item.destroy();
        this.animation?.kill();
    }

    /**
     * Whether picking this up would only carry it as a spare: a helmet, vest or backpack no better than the one worn, or
     * a second copy of a scope (as on the server). The pickup prompt shows anything else in reach first
     */
    isSparePickup(player: Player): boolean {
        const definition = this.definition;
        const inventory = UIManager.inventory;
        switch (definition.defType) {
            case DefinitionType.Armor: {
                if (definition.armorType === ArmorType.Mask) return false;
                const kind = definition.armorType === ArmorType.Helmet ? "helmet" : "vest";
                return gearPickup(definition, player.equipment[kind], inventory.spares[kind]) === "spare";
            }
            case DefinitionType.Backpack: return gearPickup(definition, player.equipment.backpack, inventory.spares.backpack) === "spare";
            case DefinitionType.Scope: {
                const count = inventory.items[definition.idString] ?? 0;
                return count > 0 && count < maxScopeCopies(definition);
            }
            default: return false;
        }
    }

    canInteract(player: Player): boolean {
        if (player.dead || player.downed) return false;

        const inventory = UIManager.inventory;
        const { weapons, lockedSlots } = inventory;
        const definition = this.definition;

        switch (definition.defType) {
            case DefinitionType.Gun: {
                let i = 0;
                for (const weapon of weapons) {
                    if (
                        weapon?.definition.defType === DefinitionType.Gun
                        && (
                            definition.idString === weapon.definition.dualVariant
                            || (
                                definition === weapon.definition
                                && weapon.definition.dualVariant
                            )
                        )
                        && !(lockedSlots & (1 << i))
                    ) {
                        return true;
                    }
                    ++i;
                }

                const activeWeaponIndex = inventory.activeWeaponIndex;
                return (!weapons[0] && !(lockedSlots & 0b01))
                    || (!weapons[1] && !(lockedSlots & 0b10))
                    || (
                        GameConstants.player.inventorySlotTypings[activeWeaponIndex] === DefinitionType.Gun
                        && definition !== weapons[activeWeaponIndex]?.definition
                        && !(lockedSlots & (1 << activeWeaponIndex))
                    )
                    // no gun in hand, both gun slots full: the first slot's gun is swapped for it (as on the server, 0.97)
                    || gunSwapSlot(weapons, activeWeaponIndex, (slot) => (lockedSlots & (1 << slot)) !== 0, definition) !== -1;
            }
            case DefinitionType.Melee: {
                return definition !== weapons[2]?.definition && !(lockedSlots & 0b100);
            }
            case DefinitionType.HealingItem:
            case DefinitionType.Ammo:
            case DefinitionType.Throwable: {
                const { idString } = definition;

                // locking the grenade slot keeps out grenades only (as on the server): healing items and ammo go in the backpack
                if (definition.defType === DefinitionType.Throwable && (lockedSlots & 0b1000)) return false;
                // (its square, and for ammo, wood and healing items every empty square besides, as on the server)
                return inventory.items[idString] + 1 <= bagRoomFor(idString, inventory.items, player.equipment.backpack);
            }
            case DefinitionType.Armor: {
                if (definition.armorType === ArmorType.Mask) return definition.level > player.maskLevel;
                // worn when it beats the piece worn, otherwise carried as the spare (as on the server)
                const kind = definition.armorType === ArmorType.Helmet ? "helmet" : "vest";
                const pickup = gearPickup(definition, player.equipment[kind], inventory.spares[kind]);
                return pickup === "wear" || pickup === "spare";
            }
            case DefinitionType.Backpack: {
                const pickup = gearPickup(definition, player.equipment.backpack, inventory.spares.backpack);
                return pickup === "wear" || pickup === "spare";
            }
            case DefinitionType.Scope: {
                // two of each found as loot: the second is a spare
                return (inventory.items[definition.idString] ?? 0) < maxScopeCopies(definition);
            }
            case DefinitionType.Skin: {
                // as the server: a skin that can't be collected isn't picked up (no "pick up" prompt for it)
                return isCollectibleSkin(definition.idString);
            }
            case DefinitionType.Perk: {
                return !PerkManager.perks[0]?.noSwap && !PerkManager.has(definition);
            }
        }
    }
}
